import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  getInvestigationReport,
  investigatedInstant,
  type IInvestigationReport,
} from '@apis/investigationReports';
import { FloorSvg } from '@components/FloorSvg/FloorSvg';
import { PageLoader } from '@components/Pages/AnalyticsPage/Reports/shared/PageLoader';
import { ReportNotFoundState } from '@components/Pages/AnalyticsPage/Reports/shared/ReportNotFoundState';
import { ReportErrorState } from '@components/Pages/AnalyticsPage/Reports/shared/ReportErrorState';
import { ReportEmptyState } from '@components/Pages/AnalyticsPage/Reports/shared/ReportEmptyState';
import { ReportHeader } from '@components/Pages/AnalyticsPage/Reports/shared/ReportHeader/ReportHeader';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ, currentLang, formatLongDate, formatYmd } from '@utils/dateUtils';
import { useStopActivitiesQuery } from '../shared/hooks/useStopActivitiesQuery';
import { useDailyRouteQuery } from './hooks/useDailyRouteQuery';
import { SubjectCard } from './components/SubjectCard';
import { StopsRail } from '../shared/StopsRail';
import { StopDetail } from './components/StopDetail';
import styles from './styles.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

/**
 * Route entry-point for the Daily Route analytics report. Reads the
 * `siteId` + `reportId` URL params, fetches the archived
 * `IInvestigationReport` (which carries the entity + date the user
 * picked at submit time), and composes the four building blocks:
 *
 *   <SubjectCard>     identifies the tag/equipment subject
 *   <StopsRail>       left-rail listbox of numbered stops
 *   <StopDetail>      middle pane with personnel/activities/equipment
 *   <FloorSvg>        right pane with the selected stop's floor highlighted
 *
 * The record is the source of truth for the report's title /
 * description (renameable inline) and for the entity + date that the
 * route compute endpoint needs. Renames PUT to the same record so
 * the change is visible across tabs and in trusstor's archive UI.
 */
export function DailyRouteReportPage() {
  const { t } = useTranslation();
  const lang = currentLang();
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  const { siteId = '', reportId = '' } = useParams<{
    siteId: string;
    reportId: string;
  }>();

  // Step 1 — fetch the archived submission. This has the entity + date
  // we need for the route-compute query, plus the title/description for
  // the renameable header. `null` from the API on 404 surfaces as the
  // not-found state.
  const recordQuery = useQuery({
    queryKey: ['investigationReport', siteId, reportId] as const,
    queryFn: ({ signal }) => getInvestigationReport(siteId, reportId, signal),
    enabled: !!siteId && !!reportId,
  });
  const record = recordQuery.data ?? null;
  const recordIsDailyRoute = record?.investigationType === 'dailyRoute';

  // Derive the route-compute params from the record. When the record
  // hasn't loaded yet (or is a different type), fall back to empty
  // strings — the underlying daily-route query is gated by `enabled` and
  // won't fire until siteId + entityId + date are non-empty.
  // A record with no `investigatedTimestamp` has no day to compute a route
  // for. Leave the params empty so the query stays dormant (rather than
  // fetching some substituted day and rendering a believable wrong route)
  // and render the explicit state below.
  const analysedInstant = recordIsDailyRoute ? investigatedInstant(record) : undefined;
  const params =
    recordIsDailyRoute && analysedInstant
      ? deriveDailyRouteParams(record, analysedInstant, tz)
      : { entityKind: 'tag' as const, entityId: '', date: '' };

  // Step 2 — route + activities queries. Same shape as before; the
  // `enabled` guards keep the network call dormant until params land.
  const route = useDailyRouteQuery({ siteId, ...params });
  const { data, isLoading, error, refetch } = route;

  const [userSelectedIndex, setUserSelectedIndex] = useState<number | null>(
    null,
  );
  // Memoized so the O(stops.length) `.some` scan only re-runs when the
  // stops set or the user's pick changes — not on every keystroke / hover
  // / background refetch that doesn't touch this derivation.
  const effectiveSelectedIndex = useMemo(() => {
    const stops = data?.stops;
    if (!stops?.length) return 1;
    const first = stops[0]!.stopIndex;
    const last = stops[stops.length - 1]!.stopIndex;
    if (userSelectedIndex == null) return first;
    if (!stops.some((s) => s.stopIndex === userSelectedIndex)) return first;
    return Math.min(userSelectedIndex, last);
  }, [data?.stops, userSelectedIndex]);

  const selected =
    data?.stops.find((s) => s.stopIndex === effectiveSelectedIndex) ?? null;
  const selectedFloorId = selected?.floorId ?? '';
  const activities = useStopActivitiesQuery({
    siteId,
    floorId: selectedFloorId,
    date: params.date,
  });

  // -- Render branches (hooks above are unconditional; only render below). --

  // Record is still loading OR not-found. Combine these into a single
  // gating branch: while loading, show the page loader; if the query
  // resolved to null OR the record is the wrong type, show not-found.
  if (recordQuery.isLoading) {
    return <PageLoader ariaLabelKey="reports.dailyRoute.title" />;
  }
  if (!record || !recordIsDailyRoute) {
    return (
      <ReportNotFoundState
        backHref={`/analytics/${siteId}`}
        notFoundKey="reports.dailyRoute.notFound"
        backKey="reports.dailyRoute.notFoundBack"
      />
    );
  }
  if (!analysedInstant) {
    return (
      <ReportNotFoundState
        backHref={`/analytics/${siteId}`}
        notFoundKey="reports.shared.noAnalysedDate"
        backKey="reports.dailyRoute.notFoundBack"
      />
    );
  }
  if (isLoading) {
    return <PageLoader ariaLabelKey="reports.dailyRoute.title" />;
  }
  if (error) {
    return (
      <ReportErrorState
        titleKey="reports.dailyRoute.errorRetry"
        retryLabelKey="reports.dailyRoute.retry"
        onRetry={() => refetch()}
      />
    );
  }
  // Both render branches below (empty stops + happy path) share the same
  // header + subject card. Hoisted here so changes to either don't need
  // to be made twice.
  const dateText = formatLongDate(params.date, lang, tz);
  const reportHeader = (
    <ReportHeader
      siteId={siteId}
      reportId={reportId}
      record={record}
      defaults={{
        title: t('reports.dailyRoute.title'),
        description: t('reports.dailyRoute.subtitle'),
      }}
      backHref={`/analytics/${siteId}`}
      backLabel={t('reports.dailyRoute.notFoundBack')}
      renameLabel={t('reports.dailyRoute.rename')}
      dateText={dateText}
    />
  );
  const subjectCard = data?.subject ? (
    <SubjectCard
      subject={data.subject}
      archivedName={record.tagName ?? null}
      reportDateText={dateText}
    />
  ) : null;

  if (!data || data.stops.length === 0) {
    return (
      <div className={styles.page}>
        {reportHeader}
        {subjectCard && <div className={styles.content}>{subjectCard}</div>}
        <ReportEmptyState
          titleKey="reports.dailyRoute.empty.title"
          descriptionKey="reports.dailyRoute.noMovement"
        />
      </div>
    );
  }

  const stop = selected ?? data.stops[0]!;

  return (
    <div className={styles.page}>
      {reportHeader}
      <div className={styles.content}>
        {subjectCard}
        <div className={styles.body}>
          <StopsRail
            stops={data.stops}
            selectedIndex={effectiveSelectedIndex}
            onSelect={setUserSelectedIndex}
            subjectName={data.subject.name}
            i18nNamespace="reports.dailyRoute"
          />
          <ScrollArea className={styles.pinned}>
            <StopDetail
              stop={stop}
              activities={{
                data: activities.data,
                isLoading: activities.isLoading,
                isError: activities.isError,
                refetch: activities.refetch,
              }}
            />
          </ScrollArea>
          <ScrollArea className={styles.pinned}>
            <div className={styles.floorPane}>
              <FloorSvg
                siteId={siteId}
                floorId={stop.floorId}
                highlightAreaId={stop.areaId}
              />
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Internal helpers                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Pull the route-compute inputs (entityKind, entityId, date) off an
 * archived `IInvestigationReport` of type `dailyRoute`.
 *
 * Date: the caller resolves the analysed instant and passes it in — the
 * "no instant recorded" case is a render state, not a value to invent here.
 * The FE always submits noon-of-day in project tz via
 * `pickerDateToProjectNoonMs`, so the YMD round-trip is stable when the
 * same site tz is used for read and write.
 */
function deriveDailyRouteParams(
  record: IInvestigationReport,
  analysedInstant: Date,
  tz: string,
): {
  entityKind: 'tag' | 'equipment';
  entityId: string;
  date: string;
} {
  const entityKind = record.entityType ?? 'tag';
  const entityId =
    entityKind === 'tag' ? record.tagId ?? '' : record.equipmentId ?? '';
  return { entityKind, entityId, date: formatYmd(analysedInstant, tz) };
}
